import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, CheckCircle2, Phone, Trash2, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABELS = {
  new: "Nový",
  contacted: "Kontaktovaný",
  confirmed: "Potvrdený",
  archived: "Archivovaný",
};

const STATUS_FLOW = ["new", "contacted", "confirmed", "archived"];

function StatusBadge({ status }) {
  const map = {
    new: "border-gold/60 text-gold",
    contacted: "border-blue-400/50 text-blue-300",
    confirmed: "border-emerald-400/50 text-emerald-300",
    archived: "border-white/20 text-cream/50",
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-1 border text-[10px] uppercase tracking-[0.2em] ${map[status] || map.new}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, confirmed: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    try {
      const [inq, st] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/stats"),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((items) => items.map((i) => (i.id === id ? res.data : i)));
      if (selected?.id === id) setSelected(res.data);
      toast.success("Stav aktualizovaný.");
      const st = await api.get("/admin/stats");
      setStats(st.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Naozaj odstrániť tento dopyt?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((items) => items.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Dopyt odstránený.");
      const st = await api.get("/admin/stats");
      setStats(st.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-cream">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <div className="w-8 h-8 border border-gold/50 flex items-center justify-center text-gold font-serif">
              T
            </div>
            <div className="leading-tight">
              <div className="font-serif text-cream">Tatra 603 · Admin</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-cream/40">
                Prehľad dopytov
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-5">
            <span className="text-xs text-cream/50 hidden sm:block">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="text-xs uppercase tracking-[0.2em] text-cream/70 hover:text-gold flex items-center gap-2 transition"
            >
              <LogOut size={14} />
              Odhlásiť
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Všetky dopyty", value: stats.total, icon: Inbox },
            { label: "Nové", value: stats.new, icon: Inbox },
            { label: "Kontaktované", value: stats.contacted, icon: Phone },
            { label: "Potvrdené", value: stats.confirmed, icon: CheckCircle2 },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                data-testid={`stat-${s.label}`}
                className="bg-[#111111] border border-white/10 p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-cream/50">
                    {s.label}
                  </span>
                  <Icon size={16} className="text-gold/80" strokeWidth={1.3} />
                </div>
                <div className="font-serif text-4xl text-cream mt-3 font-light">
                  {s.value}
                </div>
              </div>
            );
          })}
        </div>

        {/* List + Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-[#111111] border border-white/10">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="font-serif text-xl text-cream">Dopyty</h2>
              <span className="text-[11px] text-cream/40 uppercase tracking-[0.2em]">
                {inquiries.length} záznamov
              </span>
            </div>
            {loading ? (
              <div className="p-10 text-center text-cream/50">Načítava sa…</div>
            ) : inquiries.length === 0 ? (
              <div className="p-10 text-center text-cream/50">
                Zatiaľ žiadne dopyty.
              </div>
            ) : (
              <ul data-testid="inquiry-list">
                {inquiries.map((i) => (
                  <li key={i.id}>
                    <button
                      onClick={() => setSelected(i)}
                      data-testid={`inquiry-row-${i.id}`}
                      className={`w-full text-left px-6 py-5 border-b border-white/5 hover:bg-white/[0.03] transition flex items-center gap-4 ${
                        selected?.id === i.id ? "bg-white/[0.04]" : ""
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <span className="font-serif text-lg text-cream truncate">
                            {i.name}
                          </span>
                          <StatusBadge status={i.status} />
                        </div>
                        <div className="text-xs text-cream/50 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                          <span>{i.email}</span>
                          {i.event_type && <span>· {i.event_type}</span>}
                          {i.event_date && <span>· {i.event_date}</span>}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-cream/30" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-5 bg-[#111111] border border-white/10 p-6 lg:sticky lg:top-24 self-start">
            {!selected ? (
              <div className="text-cream/50 text-sm py-12 text-center">
                Vyberte dopyt zo zoznamu.
              </div>
            ) : (
              <div data-testid="inquiry-detail">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                      Detail dopytu
                    </span>
                    <h3 className="font-serif text-2xl text-cream mt-1">
                      {selected.name}
                    </h3>
                  </div>
                  <StatusBadge status={selected.status} />
                </div>

                <dl className="mt-6 space-y-4 text-sm">
                  <Detail label="E-mail" value={selected.email} />
                  <Detail label="Telefón" value={selected.phone} />
                  <Detail label="Typ udalosti" value={selected.event_type} />
                  <Detail label="Dátum" value={selected.event_date} />
                  <Detail label="Miesto" value={selected.location} />
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.25em] text-cream/50 mb-1">
                      Správa
                    </dt>
                    <dd className="text-cream/80 leading-relaxed whitespace-pre-wrap">
                      {selected.message}
                    </dd>
                  </div>
                  <Detail
                    label="Prijatý"
                    value={new Date(selected.created_at).toLocaleString("sk-SK")}
                  />
                </dl>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-cream/50 mb-3">
                    Zmeniť stav
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_FLOW.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleStatus(selected.id, s)}
                        data-testid={`status-btn-${s}`}
                        className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border transition ${
                          selected.status === s
                            ? "border-gold text-gold"
                            : "border-white/20 text-cream/60 hover:border-gold/60 hover:text-gold"
                        }`}
                      >
                        {STATUS_LABELS[s]}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handleDelete(selected.id)}
                    data-testid="inquiry-delete"
                    className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-red-400/80 hover:text-red-400 transition"
                  >
                    <Trash2 size={14} /> Odstrániť
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-[10px] uppercase tracking-[0.25em] text-cream/50 min-w-[100px]">
        {label}
      </dt>
      <dd className="text-cream/90 text-right">{value}</dd>
    </div>
  );
}
